import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Clock, Facebook, Twitter, Instagram, Award, GraduationCap } from 'lucide-react';
import { Button } from './ui/Button';
export function Layout({
  children
}: {
  children: React.ReactNode;
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showBanner, setShowBanner] = useState(true);
  const location = useLocation();
  const navigation = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'Non-Medical Transport',
    href: '/non-medical-transportation'
  }, {
    name: 'Services',
    href: '/services'
  }, {
    name: 'Shipping',
    href: '/shipping'
  }, {
    name: 'Service Area',
    href: '/service-area'
  }, {
    name: 'About',
    href: '/about'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  const isActive = (path: string) => location.pathname === path;
  return <div className="min-h-screen flex flex-col font-sans bg-white" data-id="element-36">
      {/* School Run Banner */}
      {showBanner && <div className="bg-primary-600 text-white py-2.5 px-4 relative" data-id="element-37">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-3 text-sm md:text-base font-medium" data-id="element-38">
            <GraduationCap className="w-5 h-5 flex-shrink-0 animate-bounce" data-id="element-39" />
            <span data-id="element-40">
              <strong data-id="element-41">NEW: School Runs for Kids!</strong>{' '}
              <span className="hidden sm:inline" data-id="element-42">
                Safe & reliable pick-up and drop-off for your children.{' '}
              </span>
              <a href="tel:2404132157" className="underline underline-offset-2 font-bold hover:text-white/90 transition-colors" data-id="element-43">
                Call (240) 413-2157 for affordable rates
              </a>
            </span>
            <button onClick={() => setShowBanner(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-1" aria-label="Dismiss banner" data-id="element-44">
              <X className="w-4 h-4" data-id="element-45" />
            </button>
          </div>
        </div>}

      {/* Top Utility Bar */}
      <div className="bg-black text-white py-2 px-4 text-sm hidden md:block border-b border-warm-800" data-id="element-46">
        <div className="max-w-7xl mx-auto flex justify-between items-center" data-id="element-47">
          <div className="flex items-center space-x-6" data-id="element-48">
            <a href="tel:2404132157" className="flex items-center hover:text-primary-600 transition-colors font-medium" data-id="element-49">
              <Phone className="w-4 h-4 mr-2" data-id="element-50" /> (240) 413-2157
            </a>
            <a href="tel:3013665210" className="flex items-center hover:text-primary-600 transition-colors font-medium" data-id="element-51">
              <Phone className="w-4 h-4 mr-2" data-id="element-52" /> (301) 366-5210
            </a>
          </div>
          <div className="flex items-center text-primary-600 font-semibold tracking-wide text-xs uppercase" data-id="element-53">
            <Award className="w-4 h-4 mr-2" data-id="element-54" />
            15+ Years of Experience in the Industry
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="bg-black sticky top-0 z-50" data-id="element-55">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-56">
          <div className="flex justify-between h-28" data-id="element-57">
            {/* Logo */}
            <div className="flex items-center flex-shrink-0" data-id="element-58">
              <Link to="/" className="flex items-center group" data-id="element-59">
                <img src="https://cdn.magicpatterns.com/uploads/8cvtGHTpu2TP3oAB44TxH8/tmp_60f8a779-90fc-4093-967e-c02a439baa5d.png" alt="Gozie Shipping and Transportation" className="h-28 w-auto" data-id="element-60" />
              </Link>
            </div>

            {/* Desktop Nav Links */}
            <nav className="hidden xl:flex items-center justify-center flex-1 px-8" data-id="element-61">
              <div className="flex space-x-1" data-id="element-62">
                {navigation.map(item => <Link key={item.name} to={item.href} className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive(item.href) ? 'text-primary-600 bg-white/10' : 'text-white hover:text-primary-600'}`} data-id="element-63">
                    {item.name}
                  </Link>)}
              </div>
            </nav>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center space-x-4 flex-shrink-0" data-id="element-64">
              <Link to="/booking" data-id="element-65">
                <Button variant="primary" data-id="element-66">Get a Quote</Button>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center xl:hidden space-x-4" data-id="element-67">
              <a href="tel:2404132157" className="text-primary-600 p-2 bg-white/10 rounded-full" data-id="element-68">
                <Phone className="w-5 h-5" data-id="element-69" />
              </a>
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white hover:text-primary-600 p-2 focus:outline-none" aria-expanded={isMobileMenuOpen} data-id="element-70">
                <span className="sr-only" data-id="element-71">Open main menu</span>
                {isMobileMenuOpen ? <X className="block h-7 w-7" data-id="element-72" /> : <Menu className="block h-7 w-7" data-id="element-73" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav Drawer */}
        <div className={`xl:hidden absolute w-full bg-black border-t border-warm-800 shadow-xl transition-all duration-300 ease-in-out origin-top ${isMobileMenuOpen ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'}`} data-id="element-74">
          <div className="px-4 pt-2 pb-6 space-y-1 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-75">
            {navigation.map(item => <Link key={item.name} to={item.href} onClick={() => setIsMobileMenuOpen(false)} className={`block px-4 py-3 rounded-lg text-base font-semibold ${isActive(item.href) ? 'text-primary-600 bg-white/10' : 'text-white hover:text-primary-600 hover:bg-white/5'}`} data-id="element-76">
                {item.name}
              </Link>)}
            <div className="pt-4 pb-2" data-id="element-77">
              <Link to="/booking" onClick={() => setIsMobileMenuOpen(false)} data-id="element-78">
                <Button variant="primary" fullWidth size="lg" data-id="element-79">
                  Get a Quote Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow" data-id="element-80">{children}</main>

      {/* Footer */}
      <footer className="bg-black text-white" data-id="element-81">
        <div className="py-12" data-id="element-82">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-83">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12" data-id="element-84">
              {/* Company Info */}
              <div className="space-y-6" data-id="element-85">
                <Link to="/" className="inline-block" data-id="element-86">
                  <img src="https://cdn.magicpatterns.com/uploads/8cvtGHTpu2TP3oAB44TxH8/tmp_60f8a779-90fc-4093-967e-c02a439baa5d.png" alt="Gozie Shipping and Transportation" className="h-24 w-auto" data-id="element-87" />
                </Link>
                <p className="text-sm leading-relaxed text-warm-400" data-id="element-88">
                  Providing safe, reliable, and affordable transportation and
                  shipping services for individuals, families, and businesses in
                  our community and surrounding areas.
                </p>
                <div className="flex space-x-4 pt-2" data-id="element-89">
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-90">
                    <Facebook className="w-5 h-5" data-id="element-91" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-92">
                    <Twitter className="w-5 h-5" data-id="element-93" />
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-warm-800 flex items-center justify-center text-warm-400 hover:bg-primary-600 hover:text-white transition-colors" data-id="element-94">
                    <Instagram className="w-5 h-5" data-id="element-95" />
                  </a>
                </div>
              </div>

              {/* Quick Links */}
              <div data-id="element-96">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-97">
                  Quick Links
                </h3>
                <ul className="space-y-3 text-sm font-medium" data-id="element-98">
                  <li data-id="element-99">
                    <Link to="/services" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-100">
                      Our Services
                    </Link>
                  </li>
                  <li data-id="element-101">
                    <Link to="/shipping" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-102">
                      Intl. Shipping
                    </Link>
                  </li>
                  <li data-id="element-103">
                    <Link to="/service-area" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-104">
                      Service Area
                    </Link>
                  </li>
                  <li data-id="element-105">
                    <Link to="/booking" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-106">
                      Book a Ride
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div data-id="element-107">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-108">
                  Resources
                </h3>
                <ul className="space-y-3 text-sm font-medium" data-id="element-109">
                  <li data-id="element-110">
                    <Link to="/about" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-111">
                      About Us
                    </Link>
                  </li>
                  <li data-id="element-112">
                    <Link to="/faq" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-113">
                      FAQ
                    </Link>
                  </li>
                  <li data-id="element-114">
                    <Link to="/contact" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-115">
                      Contact Us
                    </Link>
                  </li>
                  <li data-id="element-116">
                    <Link to="/privacy-policy" className="text-warm-400 hover:text-primary-600 transition-colors" data-id="element-117">
                      Privacy Policy
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Contact */}
              <div data-id="element-118">
                <h3 className="text-white font-bold text-lg mb-6 uppercase tracking-wider" data-id="element-119">
                  Contact Info
                </h3>
                <ul className="space-y-4 text-sm" data-id="element-120">
                  <li className="flex items-start" data-id="element-121">
                    <Phone className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-122" />
                    <div data-id="element-123">
                      <a href="tel:2404132157" className="text-white font-bold hover:text-primary-600 transition-colors block" data-id="element-124">
                        (240) 413-2157
                      </a>
                      <span className="text-xs text-warm-500 mt-1 block" data-id="element-125">
                        Available 24/7 for dispatch
                      </span>
                    </div>
                  </li>
                  <li className="flex items-start" data-id="element-126">
                    <MapPin className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-127" />
                    <span className="text-warm-400 leading-relaxed" data-id="element-128">
                      4215 58th Ave #5
                      <br data-id="element-129" />
                      Bladensburg, MD 20710
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-130">
                    <Clock className="w-5 h-5 mr-3 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-131" />
                    <span className="text-warm-400 leading-relaxed" data-id="element-132">
                      Open Mon – Sun
                      <br data-id="element-133" />
                      24/7 Service Available
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-warm-800 py-6 text-center text-sm text-warm-500" data-id="element-134">
          <div className="max-w-7xl mx-auto px-4" data-id="element-135">
            <p data-id="element-136">
              &copy; {new Date().getFullYear()} Gozie Shipping and
              Transportation. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}